import React, { useMemo, useState, useEffect } from 'react'
import { Modal, Button, Icon, Typography, message, Input, Card, Badge, Alert } from 'antd'
import './OutfitInUseModal.scss'

const { Text } = Typography
const { TextArea } = Input

/**
 * Helper component for each prompt instance
 */
const PromptEditor = ({ instance, onUpdate }) => {
  const [localText, setLocalText] = useState(instance.prompt)

  useEffect(() => {
    setLocalText(instance.prompt)
  }, [instance.prompt])

  const hasTag = localText.includes(instance.outfitKey)

  return (
    <div className="prompt-editor-card">
      <div className="prompt-type-header">
        <span className="type-label">
          {instance.type === 'image' ? 'Image Prompt' : 'Video Prompt'}
        </span>
        <div className={`status-badge ${hasTag ? 'warning' : 'success'}`}>
          <Icon type={hasTag ? 'warning' : 'check'} />
          {hasTag ? `Contains ${instance.outfitKey}` : 'Cleared'}
        </div>
      </div>
      <TextArea
        rows={3}
        value={localText}
        onChange={e => setLocalText(e.target.value)}
        placeholder="Edit prompt to remove outfit tag..."
      />
      <div style={{ textAlign: 'right' }}>
        <Button
          size="small"
          type="primary"
          ghost
          className="save-button"
          onClick={() => onUpdate(instance, localText)}
          disabled={instance.prompt === localText}
        >
          Save Changes
        </Button>
      </div>
    </div>
  )
}

/**
 * Modal to manage an outfit that is currently used in shots
 */
const OutfitInUseModal = ({
  visible,
  onClose,
  outfitKey,
  formData,
  setFormData,
  onDeleteOutfit
}) => {
  const { shotList = [] } = formData
  const [managedScenes, setManagedScenes] = useState([])

  // Group instances by Scene and Shot - only on open or when outfitKey changes
  useEffect(() => {
    if (visible && outfitKey) {
      const scenes = []
      
      shotList.forEach((scene, sceneIndex) => {
        const shotsInScene = []
        
        if (scene.shots) {
          scene.shots.forEach((shot, shotIndex) => {
            const inImage = shot.initial_image_prompt?.includes(outfitKey)
            const inVideo = shot.video_prompt?.includes(outfitKey)

            if (inImage || inVideo) {
              const instances = []
              // We track the type of prompt that actually HAD the tag initially
              if (inImage) {
                instances.push({
                  sceneIndex,
                  shotIndex,
                  type: 'image',
                  shotId: shotIndex + 1,
                  outfitKey
                })
              }
              if (inVideo) {
                instances.push({
                  sceneIndex,
                  shotIndex,
                  type: 'video',
                  shotId: shotIndex + 1,
                  outfitKey
                })
              }
              
              shotsInScene.push({
                shotId: shotIndex + 1,
                instances
              })
            }
          })
        }
        
        if (shotsInScene.length > 0) {
          scenes.push({
            sceneIndex,
            shots: shotsInScene
          })
        }
      })
      setManagedScenes(scenes)
    }
  }, [visible, outfitKey]) // LOCK the list when visible changes to true

  // Map managed scenes to current data from shotList
  const groupedData = useMemo(() => {
    return managedScenes.map(scene => {
      const currentScene = shotList[scene.sceneIndex]
      if (!currentScene) return scene

      return {
        ...scene,
        shots: scene.shots.map(shot => {
          const currentShot = currentScene.shots && currentScene.shots[shot.shotId - 1]
          if (!currentShot) return shot

          return {
            ...shot,
            instances: shot.instances.map(inst => ({
              ...inst,
              prompt: inst.type === 'image' ? currentShot.initial_image_prompt : currentShot.video_prompt
            }))
          }
        })
      }
    })
  }, [managedScenes, shotList])

  const handleUpdateInstance = (instance, newText) => {
    const { sceneIndex, shotIndex, type } = instance

    setFormData(prev => {
      const newShotList = [...(prev.shotList || [])]
      const scene = { ...newShotList[sceneIndex] }
      const shots = [...(scene.shots || [])]
      const shot = { ...shots[shotIndex] }

      if (type === 'image') {
        shot.initial_image_prompt = newText
      } else {
        shot.video_prompt = newText
      }

      shots[shotIndex] = shot
      scene.shots = shots
      newShotList[sceneIndex] = scene

      return { ...prev, shotList: newShotList }
    })

    message.success(`Updated prompt for Shot ${instance.shotId}`)
  }

  const handleRemoveAllAndClose = () => {
    setFormData(prev => {
      const newShotList = (prev.shotList || []).map(scene => ({
        ...scene,
        shots: (scene.shots || []).map(shot => {
          const keyPattern = new RegExp(
            outfitKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
            'g'
          )
          return {
            ...shot,
            initial_image_prompt: (shot.initial_image_prompt || '')
              .replace(keyPattern, '')
              .replace(/\s+/g, ' ')
              .trim(),
            video_prompt: (shot.video_prompt || '')
              .replace(keyPattern, '')
              .replace(/\s+/g, ' ')
              .trim()
          }
        })
      }))
      return { ...prev, shotList: newShotList }
    })
    message.success(`Removed all instances of ${outfitKey}`)
  }

  const allInstances = groupedData.flatMap(s =>
    s.shots.flatMap(sh => sh.instances)
  )
  const instancesWithKey = allInstances.filter(i =>
    i.prompt.includes(outfitKey)
  )
  const canDelete = instancesWithKey.length === 0

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ 
            background: '#e6f7ff', 
            width: '32px', 
            height: '32px', 
            borderRadius: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Icon type="skin" style={{ color: '#1890ff', fontSize: '18px' }} />
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Cleanup Outfit</div>
            <div style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>Instances of: <span style={{ color: '#3b82f6' }}>{outfitKey}</span></div>
          </div>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={760}
      centered
      className="outfit-usage-modal"
      footer={
        <div className="footer-buttons">
          <Button onClick={onClose}>Close</Button>
          {!canDelete && (
            <Button type="danger" ghost onClick={handleRemoveAllAndClose}>
              Auto-Remove from All
            </Button>
          )}
          <Button
            type="primary"
            danger
            disabled={!canDelete}
            onClick={() => {
              onDeleteOutfit(outfitKey)
              onClose()
            }}
          >
            Delete Outfit
          </Button>
        </div>
      }
    >
      <div style={{ padding: '0 0 24px 0' }}>
        <Alert
          type="info"
          showIcon
          message={
            <span>
              This outfit tag is present in <b>{instancesWithKey.length}</b>{' '}
              prompts. Please remove the tag manually or use the auto-remove tool
              below.
            </span>
          }
          style={{ borderRadius: '10px', background: '#f0f7ff', border: '1px solid #e0f0ff' }}
        />
      </div>

      <div className="usage-container">
        {groupedData.length > 0 ? (
          groupedData.map(scene => (
            <Card 
              key={`scene-${scene.sceneIndex}`} 
              className="scene-card"
              title={
                <div className="scene-header">
                  <Icon type="database" />
                  <h4 className="scene-title">Scene {scene.sceneIndex + 1}</h4>
                </div>
              }
              bodyStyle={{ padding: '20px' }}
            >
              {scene.shots.map(shot => (
                <div
                  key={`shot-${scene.sceneIndex}-${shot.shotId}`}
                  className="shot-item"
                >
                  <div className="shot-header">
                    <Icon type="video-camera" />
                    <h5 className="shot-title">Shot {shot.shotId}</h5>
                  </div>
                  <div className="shot-content">
                    {shot.instances.map(instance => (
                      <PromptEditor
                        key={`${scene.sceneIndex}-${shot.shotId}-${instance.type}`}
                        instance={instance}
                        onUpdate={handleUpdateInstance}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          ))
        ) : (
          <div className="empty-state">
            <Icon type="check-circle" theme="filled" />
            <div className="success-text">
              <Typography.Title level={4} style={{ marginBottom: '8px' }}>
                All Cleaned Up!
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: '15px' }}>
                The outfit tag is no longer being used. You can safely delete
                it.
              </Typography.Text>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default OutfitInUseModal
